import { useEffect, useState } from 'react';
import axios from '../api/appAuthenticatedApi';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { resetUserInformation } from '../Redux/UserInformationSlice';
import { resetUserPermission, setUserPermission } from '../Redux/UserPermissionSlice';
import jwt_decode from 'jwt-decode';

let verifiedSessionKey = null;
let verificationPromise = null;
let verificationPromiseKey = null;

const buildLoginRedirectPath = () => {
  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;

  if (currentPath.startsWith('/login')) {
    return currentPath;
  }

  return `/login?redirect=${encodeURIComponent(currentPath)}`;
};

const LoginCheckRoute = ({ children }) => {
  const userInformation = useSelector((state) => state.userInformation.value);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isAllowed, setAllowed] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const logoutAndRedirect = () => {
      dispatch(resetUserInformation());
      dispatch(resetUserPermission());
      navigate(buildLoginRedirectPath(), { replace: true });
    };

    if (!userInformation?.userID || !userInformation?.token) {
      verifiedSessionKey = null;
      verificationPromise = null;
      verificationPromiseKey = null;
      logoutAndRedirect();
      return () => {
        isMounted = false;
      };
    }

    const currentSessionKey = `${userInformation.userID}:${userInformation.token}`;

    if (verifiedSessionKey === currentSessionKey) {
      setAllowed(true);
      return () => {
        isMounted = false;
      };
    }

    if (!verificationPromise || verificationPromiseKey !== currentSessionKey) {
      verificationPromiseKey = currentSessionKey;
      verificationPromise = axios
        .post('/api/app/auth/verifyApp', {
          token: userInformation.token,
        })
        .then(() => {
          const decoded = jwt_decode(userInformation.token);
          if (Array.isArray(decoded.elements)) {
            dispatch(setUserPermission(decoded.elements));
          }
          verifiedSessionKey = currentSessionKey;
        })
        .catch((error) => {
          verifiedSessionKey = null;
          throw error;
        })
        .finally(() => {
          verificationPromise = null;
          verificationPromiseKey = null;
        });
    }

    verificationPromise
      .then(() => {
        if (isMounted) {
          setAllowed(true);
        }
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }

        if (error?.response && [401, 403, 431].includes(error.response.status)) {
          console.warn('APP session invalid, redirecting to login.');
          logoutAndRedirect();
          return;
        } else {
          console.error('Token verification request failed:', error);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [userInformation?.userID, userInformation?.token, dispatch, navigate]);

  return isAllowed ? children : null;
};

export default LoginCheckRoute;
